import styles from './ViewEditProject.module.css';

import deleteIcon from 'assets/svg/delete-01.svg';

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { projectsStore } from '../../../../store/mobx/projects.store';
import { getProjectById } from '../../../../services/projects.services';
import useAlert from 'hooks/useAlert';
import Tooltip from '../../../../ui/Tooltip';
import RegularButton from '../../../../ui/RegularButton';
import IconButton from '../../../../ui/IconButton';
import Tag from '../../../../ui/Tag';
import UpsertProjectModal from '../UpsertProjectModal';
import EditProjectIterationsModal from './EditProjectIterationsModal';
import EditProjectDocumentModal from './EditProjectDocumentModal';
import EditProjectDevicesModal from './EditProjectDevicesModal';
import EditProjectPerformersModal from './EditProjectPerformersModal';
import SchemeInfo from './SchemeInfo';
import DocumentsInfo from './DocumentsInfo';
import ProjectDevices from './ProjectDevices';
import ProjectPerformers from './ProjectPerformers';
import classNames from 'classnames';
import Production from './Production';

const ViewEditProject = observer(() => {
  const { showAlert } = useAlert();

  const { selectedProject, setSelectedProject, removeProject, resetProject, setIsLoading } =
    projectsStore;
  const [projectEditModalOpened, setProjectEditModalOpened] = useState(false);
  const [iterationEditModalOpened, setIterationEditModalOpened] = useState(false);
  const [documentEditModalOpened, setDocumentEditModalOpened] = useState(false);
  const [projectDevicesEditModalOpened, setProjectDevicesEditModalOpened] = useState(false);
  const [performerEditModalOpened, setPerformerEditModalOpened] = useState(false);

  useEffect(() => {
    void loadProject();
  }, [selectedProject?.id]);

  const loadProject = async () => {
    if (selectedProject?.id) {
      setIsLoading(true);
      const result = await getProjectById(selectedProject.id);
      if (result) setSelectedProject(result.data);
      setIsLoading(false);
    }
  };

  const handleEditClick = () => setProjectEditModalOpened(true);
  const handleDeleteClick = () =>
    sureDeleteProjectAlert(async () => {
      const result = await removeProject();
      if (!result.success) {
        return errorDeleteProjectAlert(result.error_code, result.error);
      }
      resetProject();
    });

  const sureDeleteProjectAlert = (callback) =>
    showAlert(`Вы действительно хотите удалить данный проект?`, 'sure', callback);

  const errorDeleteProjectAlert = (code, message) => {
    showAlert(` Код № ${code}${message ? ` - ${message}` : ''}!`, 'error');
  };

  const Toolbar = () => (
    <div className={styles.toolbar}>
      <div className={styles.wrapperActions}>
        <RegularButton text={'Редактировать'} variant={'flat'} onClick={handleEditClick} />
        <IconButton
          tooltip={'Удалить'}
          variant={'outlined'}
          icon={deleteIcon}
          onClick={handleDeleteClick}
        />
      </div>
      <div className={styles.titleContainer}>
        <Tag text={selectedProject?.code} color={'secondary'} variant="outlined" />
        <div className={styles.tooltipContainer}>
          <Tooltip text={selectedProject?.name} styleConfig={{ tooltip: styles.titleTooltip }}>
            <div className={styles.titleProject}>{selectedProject?.name}</div>
          </Tooltip>
        </div>
      </div>
    </div>
  );

  const Goals = () => (
    <>
      <div className={styles.heading}>Цели:</div>
      {(selectedProject?.goal && (
        <div className={styles.textContent}>{selectedProject?.goal}</div>
      )) || (
        <div className={classNames(styles.textContent, styles.colorSecondary)}>
          Цели не добавлены
        </div>
      )}
    </>
  );

  const Tasks = () => (
    <>
      <div className={styles.heading}>Задачи:</div>
      {(selectedProject?.task && (
        <div className={styles.textContent}>{selectedProject?.task}</div>
      )) || (
        <div className={classNames(styles.textContent, styles.colorSecondary)}>
          Задачи не добавлены
        </div>
      )}
    </>
  );

  const Description = () => (
    <div className={styles.description}>
      {(selectedProject?.description && (
        <span className={styles.descriptionContent}>{selectedProject?.description}</span>
      )) || <span className={styles.descriptionContent}>Аннотации не добавлены</span>}
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      <Toolbar />
      <div className={styles.projectContainer}>
        <Goals />
        <Tasks />
        <Description />
        <div className={styles.cardsWrapper}>
          <SchemeInfo />
          <Production setModalOpened={setIterationEditModalOpened} />
          <DocumentsInfo setModalOpened={setDocumentEditModalOpened} />
          <ProjectPerformers setModalOpened={setPerformerEditModalOpened} />
          <ProjectDevices setModalOpened={setProjectDevicesEditModalOpened} />
        </div>
      </div>
      {projectEditModalOpened && <UpsertProjectModal setModalOpened={setProjectEditModalOpened} />}

      {iterationEditModalOpened && (
        <EditProjectIterationsModal setModalOpened={setIterationEditModalOpened} />
      )}
      {documentEditModalOpened && (
        <EditProjectDocumentModal setModalOpened={setDocumentEditModalOpened} />
      )}
      {performerEditModalOpened && (
        <EditProjectPerformersModal setModalOpened={setPerformerEditModalOpened} />
      )}
      {projectDevicesEditModalOpened && (
        <EditProjectDevicesModal setModalOpened={setProjectDevicesEditModalOpened} />
      )}
    </div>
  );
});

export default ViewEditProject;
